import React, { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { FiUsers, FiSettings, FiPlus, FiUser } from "react-icons/fi";
import { motion } from "framer-motion";

// Map route paths to permission keys
const routePermissions = {
  "/add-employee": "create_teammember",
  "/employee-list": "list_teammember",
  "/customer-support": "customer_support",
  "/com-tickets": "company_tickets",
  "/sup-tickets": "support_tickets",
  "/employee-performance": "employee_performance",
};

// TicketCard Component
function TicketCard({ ticket, index }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      whileHover={{
        y: -5,
        boxShadow:
          "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
      }}
      className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 flex flex-col p-4"
    >
      <h3 className="text-lg font-semibold text-gray-800 mb-2">{ticket.subject}</h3>
      <p className="text-gray-600 line-clamp-2 mb-2">{ticket.description}</p>

      {ticket.image && ticket.image.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-2">
          {ticket.image.map((imgUrl, i) => (
            <a
              key={i}
              href={imgUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-20 h-20 rounded-md overflow-hidden border border-gray-200"
            >
              <img
                src={imgUrl}
                alt={`ticket-${i}`}
                className="w-full h-full object-cover"
                onError={(e) => (e.target.style.display = "none")}
              />
            </a>
          ))}
        </div>
      )}

      <span className="text-sm text-gray-500">
        Status: {ticket.status || "N/A"}
      </span>
    </motion.div>
  );
}

function FranchiseDashboard({ user, tickets }) {
  const [activeCategory, setActiveCategory] = useState(null);

  // Filter cards based on user permissions
  const cardCategories = [
    {
      title: "Employee Management",
      icon: <FiUsers className="text-blue-500 text-lg" />,
      cards: [
        { path: "/add-employee", title: "Add Employee", icon: <FiPlus /> },
        { path: "/employee-list", title: "Employee List", icon: <FiUsers /> },
      ],
    },
    {
      title: "Support",
      icon: <FiSettings className="text-red-500 text-lg" />,
      cards: [
        { path: "/customer-support", title: "Customer Support", icon: <FiUsers /> },
        { path: "/com-tickets", title: "Company Tickets", icon: <FiUser /> },
        { path: "/sup-tickets", title: "Support Tickets", icon: <FiUser /> },
        { path: "/employee-performance", title: "Employee Performance", icon: <FiUser /> },
      ],
    },
  ];

  const filteredCategories = cardCategories
    .map((cat) => ({
      ...cat,
      cards:
        user.role === "company"
          ? cat.cards
          : cat.cards.filter((card) =>
              user.accessPermissions.includes(routePermissions[card.path])
            ),
    }))
    .filter((cat) => cat.cards.length > 0);

  // Access control: if user doesn't have permission for tickets page, hide
  const canAccessTickets = user.role === "company" || user.accessPermissions.includes("support_tickets");

  if (!canAccessTickets) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Top Navbar */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <h1 className="text-xl font-bold text-gray-800">HRMS Dashboard</h1>

          {/* Categories Navbar */}
          <div className="flex space-x-6">
            {filteredCategories.map((category, index) => (
              <div
                key={index}
                className="relative group"
                onMouseEnter={() => setActiveCategory(index)}
                onMouseLeave={() => setActiveCategory(null)}
              >
                <button className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium">
                  {category.icon}
                  <span>{category.title}</span>
                </button>

                {activeCategory === index && (
                  <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg p-2 z-50">
                    {category.cards.map((card, i) => (
                      <Link
                        key={i}
                        to={card.path}
                        className="flex items-center p-2 rounded-md hover:bg-gray-100 text-gray-700 hover:text-blue-600"
                      >
                        <span className="mr-2">{card.icon}</span>
                        {card.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tickets Section */}
      <div className="max-w-7xl mx-auto p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tickets && tickets.length > 0 ? (
          tickets.map((ticket, i) => <TicketCard key={ticket._id} ticket={ticket} index={i} />)
        ) : (
          <p className="text-gray-500">No tickets available.</p>
        )}
      </div>
    </div>
  );
}

export default FranchiseDashboard;
